export function getProperty(feature, key) {
  return feature.get(key) || getPropertyEndingWith(feature, "-" + key);
}
export function getPropertyEndingWith(feature, keySuffix) {
  const key = feature
    .getKeys()
    .filter((key) => key.endsWith(keySuffix))
    .filter((key) => !key.startsWith("revision-"))[0];
  return feature.get(key);
}

export function getPropertyEndingWithExcludingLayer7(feature, keySuffix) {
  const key = feature
    .getKeys()
    .filter((key) => !key.startsWith("7-"))
    .filter((key) => !key.startsWith("osm-"))
    .filter((key) => !key.startsWith("revision-"))
    .filter((key) => key.endsWith(keySuffix))[0];
  return feature.get(key);
}

export function getPropertyMatching(feature, regExpString) {
  const regExp = new RegExp(regExpString);
  const key = feature.getKeys().filter((key) => regExp.test(key))[0];
  return feature.get(key);
}

/**
 * @typedef {Object} Tags
 * @property {ReturnType<typeof getPropertyEndingWith>} hdMap
 * @property {ReturnType<typeof getPropertyEndingWith>} laneDirection
 * @property {ReturnType<typeof getPropertyEndingWith>} laneFunction
 * @property {ReturnType<typeof getPropertyEndingWith>} hovLane
 * @property {ReturnType<typeof getPropertyEndingWith>} hov
 * @property {ReturnType<typeof getPropertyEndingWith>} vehicleRestrictionBus
 * @property {ReturnType<typeof getPropertyEndingWith>} vehicleRestrictionHGV
 * @property {ReturnType<typeof getPropertyEndingWith>} vehicleRestrictionMotorVehicle
 * @property {ReturnType<typeof getPropertyEndingWith>} vehicleRestrictionMotorcar
 * @property {ReturnType<typeof getPropertyEndingWith>} vehicleRestrictionPSV
 * @property {ReturnType<typeof getPropertyEndingWith>} vehicleRestrictionTaxi
 * @property {ReturnType<typeof getPropertyEndingWith>} laneBorder
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormMotorVehicle
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormMotorCar
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormHgv
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormPsv
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormBus
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormTaxi
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormHov
 */

/**
 * Extracts properties from a feature.
 * @param {any} feature - The feature object.
 * @returns {Tags} The extracted tag properties.
 */
export function extractTags(feature) {
  /** @type {Tags} */
  const tags = {
    hdMap: getProperty(feature, "hdmap"),
    laneDirection: getProperty(feature, "lane_direction"),
    laneFunction: getProperty(feature, "lane_function"),
    rm_hovLanes: getPropertyMatching(feature, "^\\d+-hov:lanes.*"),
    rm_vehicleAccessBus: getPropertyMatching(
      feature,
      "^\\d+-lanes:vehicle_access:bus.*",
    ),
    rm_vehicleAccessTaxi: getPropertyMatching(
      feature,
      "^\\d+-lanes:vehicle_access:taxi.*",
    ),
    rm_vehicleAccessMotorcar: getPropertyMatching(
      feature,
      "^\\d+-lanes:vehicle_access:motorcar.*",
    ),
    rm_vehicleAccessHgv: getPropertyMatching(
      feature,
      "^\\d+-lanes:vehicle_access:hgv.*",
    ),
    hovLane: getProperty(feature, "hov_lane"),
    hov: getProperty(feature, "hov"),
    vehicleRestrictionBus: getProperty(feature, "vehicle_restriction:bus"),
    vehicleRestrictionHGV: getProperty(feature, "vehicle_restriction:hgv"),
    vehicleRestrictionMotorVehicle: getProperty(
      feature,
      "vehicle_restriction:motor_vehicle",
    ),
    vehicleRestrictionMotorcar: getProperty(
      feature,
      "vehicle_restriction:motorcar",
    ),
    vehicleRestrictionPSV: getProperty(feature, "vehicle_restriction:psv"),
    vehicleRestrictionTaxi: getProperty(feature, "vehicle_restriction:taxi"),
    laneBorder:
      getProperty(feature, "lane_border") ||
      getProperty(feature, "lane_border:step"),
    lanesNormMotorVehicle: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:motor_vehicle",
    ),
    lanesNormMotorCar: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:motor_car",
    ),
    lanesNormHgv: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:hgv",
    ),
    lanesNormPsv: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:psv",
    ),
    lanesNormBus: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:bus",
    ),
    lanesNormTaxi: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:taxi",
    ),
    lanesNormHov: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:hov",
    ),
  };
  return tags;
}
